import { Component, computed, inject, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule, MatLabel, MatHint } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { User } from '../../../../../core/models/user-model';
import { CheckService } from '../../../../../core/checks/check';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { ValidationService } from '../../../../../core/common/utils/validation';
import { PendingCheckFacadeStore } from '../../store/pending-check-facade.store';
import { PendingCheckPermissionService } from '../../../services/pending-check-permission';

@Component({
  selector: 'app-check-info-card',
  imports: [
    MatCardModule,
    MatChipsModule,
    MatButtonModule,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatLabel,
    MatHint,
    MatTooltip,
    ClipboardModule,
    DatePipe,
    FormsModule,
  ],
  providers: [DatePipe],
  templateUrl: './check-info-card.html',
  styleUrl: './check-info-card.scss',
})
export class CheckInfoCard {
  readonly data = inject(PendingCheckFacadeStore);
  readonly permission = inject(PendingCheckPermissionService);
  private readonly checkService = inject(CheckService);
  private readonly snackbarService = inject(SnackbarService);

  readonly cardEditMode = signal(false);

  readonly draftSourceType = signal<'sharepoint' | 'directoryP'>('sharepoint');

  readonly draftDisplayName  = signal<string>('');
  readonly draftSharepoint   = signal<string>('');
  readonly draftDirectoryP   = signal<string>('');
  readonly draftChecker11    = signal<string | null>(null);
  readonly draftChecker12    = signal<string | null>(null);
  readonly draftDueDate1     = signal<Date | null>(null);
  readonly draftChecker2     = signal<string | null>(null);
  readonly draftDueDate2     = signal<Date | null>(null);

  /**
   * Benutzer-Liste für den Kontrolle-1-2-Select:
   * - Normal: nur DEPARTMENT_DEPUTY_MANAGER
   * - Gutachterliche Tätigkeit: DEPARTMENT_DEPUTY_MANAGER + aktuell eingeloggter User
   */
  readonly checker12Users = computed(() =>
    this.data.checkInfoViewModel()?.isConsultant
      ? this.data.usersForChecker12Consultant()
      : this.data.usersForChecker12()
  );

  readonly sharepointUrl = computed(() => {
    const path = this.data.checkInfoViewModel()?.sharepointFolderPath;
    return ValidationService.isValidUrl(path) && path?.trim() ? path : null;
  });

  readonly sharepointDraftError = computed(() => {
    return ValidationService.hasUrlError(this.draftSharepoint());
  });

  /** Prüffrist 1 ist Pflichtfeld */
  readonly draftDueDate1Missing = computed(() => {
    return this.cardEditMode() && this.draftDueDate1() == null;
  });

  /** Prüffrist 2 darf nicht vor Prüffrist 1 liegen */
  readonly draftDueDateError = computed(() => {
    const d1 = this.draftDueDate1();
    const d2 = this.draftDueDate2();
    if (d1 == null || d2 == null) return false;
    return d2 < d1;
  });

  /** Speichern nur möglich wenn keine Validierungsfehler */
  readonly canSaveEdit = computed(() => {
    if (this.sharepointDraftError()) return false;
    if (this.draftDueDate1Missing()) return false;
    if (this.draftDueDateError()) return false;
    return true;
  });

  enterCardEditMode(): void {
    const vm = this.data.checkInfoViewModel();
    if (!vm) return;
    this.draftDisplayName.set(vm.displayName ?? '');
    this.draftSharepoint.set(vm.sharepointFolderPath ?? '');
    this.draftDirectoryP.set(vm.directoryPFolderPath ?? '');
    this.draftChecker11.set((vm.checker11 as User | null)?.oid ?? null);
    this.draftChecker12.set((vm.checker12 as User | null)?.oid ?? null);
    this.draftDueDate1.set(vm.dueDate1 instanceof Date ? vm.dueDate1 : null);
    this.draftChecker2.set((vm.checker2 as User | null)?.oid ?? null);
    this.draftDueDate2.set(vm.dueDate2 instanceof Date ? vm.dueDate2 : null);
    this.draftSourceType.set(
      vm.directoryPFolderPath?.trim() ? 'directoryP' : 'sharepoint'
    );
    this.cardEditMode.set(true);
  }

  onDraftSourceTypeChange(type: 'sharepoint' | 'directoryP'): void {
    this.draftSourceType.set(type);
    if (type === 'sharepoint') {
      this.draftDirectoryP.set('');
    } else {
      this.draftSharepoint.set('');
    }
  }

  saveCardEdit(): void {
    const check = this.data.currentCheck();
    if (!check) return;
    if (!this.canSaveEdit()) return;

    const spValue = this.draftSourceType() === 'sharepoint' ? this.draftSharepoint() : '';
    const dpValue = this.draftSourceType() === 'directoryP' ? this.draftDirectoryP() : '';

    forkJoin([
      this.checkService.updateCheckApi(check.id, {
        description:          this.draftDisplayName(),
        sharepointFolderPath: spValue,
        directoryPFolderPath: dpValue,
      }),
      this.checkService.updateCheckStatusApi(check.id, {
        checkerOid1:  this.draftChecker11() ?? undefined,
        checkerOid12: this.draftChecker12() ?? null,
        checkerOid2:  this.draftChecker2()  ?? null,
        dueDate1:     this.draftDueDate1()?.toISOString() ?? undefined,
        // Fachlich keine eigene Prüffrist 1-2 – K1-2 teilt die Frist von K1-1.
        // TODO: Backend-Anpassung prüfen, ob dueDate12 aus dueDate1 abgeleitet werden kann.
        dueDate12:    this.draftDueDate1()?.toISOString() ?? null,
        dueDate2:     this.draftDueDate2()?.toISOString() ?? null,
      }),
    ]).subscribe({
      next: () => {
        this.data.refreshCheck();
        this.cardEditMode.set(false);
      },
      error: err => {
        console.error('Speichern fehlgeschlagen', err);
        this.snackbarService.info('Speichern fehlgeschlagen.');
      },
    });
  }

  cancelCardEdit(): void {
    this.cardEditMode.set(false);
  }

  onPathCopied(success: boolean): void {
    if (success) {
      this.snackbarService.info('Pfad kopiert!');
    }
  }
}


